import React, { useState, useEffect } from "react";

import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../components/Form/Button";
import ConfirmChangeStatusModal from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { AccessSettings } from "../../../../../../models/access.settings";
import { Row } from "../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import AccessSettingsModal from "./AccessModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
    changeStatus: boolean
  }
}

const AccessSettingsPage = (props: Props) => {
  const {permission} = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const {
    accesses,
    page,
    pageSize,
    total,
    hasErrors,
    isLoading,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createAccessSettings,
    updateAccessSettings,
  } = useActions();

  const [access, setAccess] = useState<AccessSettings>();
  const [openModal, setOpenModal] = useState<boolean>(false);

  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
      `}
    >
      {status}
    </Badge>
  );

  const editAccess = (index: number) => {
    const findAccess = accesses.find((access) => access.id === Number(index));

    if (findAccess) {
      if (hasErrors !== undefined) setHasErrors(undefined);
      setAccess(findAccess);
      setOpenModal(true);
    }
  };

  const changeAccessStatus = (index: number) => {
    const findAccess = accesses.find((access) => access.id === Number(index));

    if (findAccess) {
      updateAccessSettings({
        id: Number(index),
        access: {
          ...findAccess,
          status: findAccess.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeButtonStatus}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveAccessSelected = (rowsArr: number[]) => {
    const response = onSaveItemSelected(rowsArr, rows, accesses);

    setRows([...response]);
  };

  const uppercaseFirstLetter = (type: string): string =>
    type.charAt(0).toUpperCase() + type.slice(1);

  const columns: Column[] = [
    { name: "name", title: translate("name") },
    {
      name: "type",
      title: translate("type"),
      formatText: (type: string) => uppercaseFirstLetter(type),
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
      hideSearch: true,
    },
  ];

  const rowActions: RowActions = {
    orderBy: ["handleEdit", "handleChange"],
    handleEdit: permission && isShowAction(currentUser, permission.edit)
      ? (accessId) => editAccess(Number(accessId))
      : undefined,
    handleChange: permission && isShowAction(currentUser, permission.changeStatus)
      ? (accessId) => changeAccessStatus(Number(accessId))
      : undefined,
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission.create)
      ? {
        text: translate("create"),
        action: () => {
          if (hasErrors !== undefined) setHasErrors(undefined);
          setAccess(undefined);
          setOpenModal(true);
        },
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        { name: translate("name"), column: "name" },
        { name: translate("type"), column: "type" },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: permission && isShowAction(currentUser, permission.changeStatus)
      ? renderButtonToChangeStatus()
      : undefined,
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.access",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="accessSettingsList">
      <ShipeeziNewGrid
        data={accesses}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={clearSelection}
        handleSelect={(rowsArr: number[]) => handleSaveAccessSelected(rowsArr)}
        rowActions={rowActions}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />

      {openModal && (
        <AccessSettingsModal
          access={access}
          open={openModal}
          hasErrors={hasErrors}
          isLoading={isLoading}
          closeModal={() => setOpenModal(false)}
          submit={(access) => {
            if (access.id) updateAccessSettings({ id: access.id, access });
            else
              createAccessSettings({
                ...access,
                companyId: currentUser?.companyId,
              });
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatusModal
          open={openConfirmation}
          rows={rows}
          hasErrors={hasErrors}
          isLoading={isLoading}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateAccessSettings({
                id: rowId,
                access: { status } as AccessSettings,
              });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default AccessSettingsPage;
